<script setup lang="ts">
  import { useDocument } from '@/composable/useDocument'
  import { router } from '@/router'
  import { useDocumentStore } from '@/store/document'
  import { DocumentStatus, featureFlags, loadFonts, RedirectModal, requestGeolocationString } from '@gohighlevel/ghl-proposals-common'
  import { UISpin, UITextXsMedium } from '@gohighlevel/ghl-ui'
  import { computed, onMounted, provide, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'

  const { t } = useI18n()

  const route = useRoute()
  const pageLoading = ref(false)
  const store = useDocumentStore()
  const hasCertificate = computed(() => !!store.certificate?._id && !store.certificate?.deleted)
  const props = defineProps({
    childLoading: { type: Boolean, default: false },
    isPreview: { type: Boolean },
    isPDFView: { type: Boolean },
    isMobilePreview: { type: Boolean, default: false },
    loaderText: {
      type: String,
    },
    skipDocumentFetch: { type: Boolean, default: false },
  })
  const {
    loading,
    getDocumentData,
    redirectingToInvoice,
    redirectingToRedirectUrl,
    secondsTillRedirectToPayment,
    secondsTillRedirectToRedirectUrl,
    validatingInvoice,
    redirectUrl,
    documentVersion,
    maxSecondsTillRedirect,
  } = useDocument()

  provide('isPreview', props.isPreview)
  provide('isPDFView', props.isPDFView)
  provide('isMobilePreview', props.isMobilePreview)

  const AllowOldPreviewPageFeatureFlag = featureFlags.ALLOW_OLD_PREVIEW_PAGE

  /** Geolocation is only for accept/decline payloads; skip if the viewer cannot sign or has nothing left to complete. */
  function shouldRequestGeolocationForSigning(): boolean {
    if (store.isCcRecipient) return false
    if (!store.isEditable) return false
    if (!store.hasPrevRecipientsCompleted) return false
    if (store.activeRecipient?.hasCompleted) return false
    return store.assignedElementsForRecipient.length > 0
  }

  onMounted(async () => {
    if (!props.skipDocumentFetch) {
      pageLoading.value = true
      await getDocumentData(props.isPreview, props.isPDFView, props.isMobilePreview, AllowOldPreviewPageFeatureFlag)
      if (documentVersion.value > 1) {
        loadFonts(store.fontsToLoad)
      }
      pageLoading.value = false
      const isLiveSigningContext = !props.isPreview && !props.isPDFView && !props.isMobilePreview
      if (isLiveSigningContext && shouldRequestGeolocationForSigning()) {
        const positionStr = await requestGeolocationString()
        store.setUserPosition(positionStr)
      } else {
        store.setUserPosition(null)
      }
      if (store?.status === DocumentStatus.DRAFT && !props.isPreview && !props.isPDFView) {
        router.push({
          name: 'document-error',
          params: { type: 'draft_state' },
          query: {
            documentId: route.params.documentId,
            recipientId: route.params.recipientId,
          },
        })
      }
    }
  })
  const isLoading = computed(() => pageLoading.value || props.childLoading)

  const redirectModalTitle = computed(() => {
    return `${t('documentViewer.redirectingToInvoice')}...`
  })

  const redirectModalDescription = computed(() => {
    return `${t('documentViewer.loadingInvoice')}...`
  })

  const loadingText = computed(() => {
    if (props.loaderText) {
      return props.loaderText
    }
    if (validatingInvoice.value) {
      return `${t('documentViewer.loadingInvoice')}...`
    }
    if (redirectingToInvoice.value) {
      return `${t('documentViewer.redirectingToPayment')} (${secondsTillRedirectToPayment.value})sec...`
    }
    if (redirectingToRedirectUrl.value) {
      return `${t('documentViewer.redirectUrlTitle')} (${secondsTillRedirectToRedirectUrl.value})sec...`
    }
    return `${t('documentViewer.loadingDocument')}...`
  })
</script>

<template>
  <div
    class="h-screen w-screen bg-white z-[99999] flex justify-items-center justify-center fixed flex-col"
    v-if="isLoading && !redirectingToRedirectUrl && !redirectingToInvoice && !validatingInvoice"
  >
    <UISpin />
    <UITextXsMedium class="inline-flex items-center justify-center mt-4 text-gray-500">{{ loadingText }}</UITextXsMedium>
  </div>

  <RedirectModal
    v-if="(validatingInvoice || redirectingToInvoice) && !redirectingToRedirectUrl"
    :seconds-till-redirect-to-payment="secondsTillRedirectToPayment"
    :redirect-modal="validatingInvoice || redirectingToInvoice"
    :title="redirectModalTitle"
    :description="redirectModalDescription"
    :max-seconds-till-redirect-to-payment="maxSecondsTillRedirect"
  />

  <div class="h-screen w-screen bg-white z-[99999] flex justify-items-center justify-center fixed flex-col" v-if="redirectingToRedirectUrl">
    <div class="flex flex-col items-center text-center mt-4">
      <div class="items-center justify-center">
        <img src="/redirect.png" aria-hidden="true" :alt="t('documentViewer.redirectIconAlt')" class="w-42 h-32 mb-2" />
      </div>
      <UITextXsMedium class="inline-flex items-center justify-center my-4 text-gray-500">{{ loadingText }}</UITextXsMedium>
      <UITextXsMedium class="text-gray-500">
        {{ t('documentViewer.redirectUrlDescription') }}
        <a :href="redirectUrl" class="text-blue-500 underline">{{ redirectUrl }}</a>
      </UITextXsMedium>
      <UITextXsMedium class="text-gray-500 mt-2">
        {{ t('documentViewer.redirectUrlSecondDescription') }}
      </UITextXsMedium>
    </div>
  </div>
  <slot :loading="loading" :page-loading="isLoading" :has-certificate="hasCertificate" />
</template>
